<template>
    <div :class="{
            'icon-drawer': true,
            'icon-drawer--expanded': expanded,
            'icon-drawer--collapsing': !expanded && !collapseTransitionComplete,
            'icon-drawer--centered': !expanded && collapseTransitionComplete,
        }"
         :style="drawerStyle">
        <!-- Top icons: scrollable when they run out of room -->
        <div class="icon-drawer__top" ref="topEl">
            <div class="icon-drawer__list">
                <template v-for="(item, index) in topItems" :key="item.id ?? index">
                    <div v-if="item.separator"
                         class="icon-drawer__separator"
                         :aria-hidden="true" />
                    <NavDrawerIcon v-else
                                   :item="item"
                                   :row-data="item.rowData"
                                   :control-data="item.controlData ?? {}"
                                   :model-value="modelValue"
                                   :expanded="expanded"
                                   :dense="dense"
                                   :preview-mode="previewMode"
                                   :name="namePrefix ? `${namePrefix}_top_${index}` : `top_${index}`"
                                   :root="root"
                                   :scopeitems="scopeitems"
                                   :controlscope="controlscope"
                                   @update:model-value="(v) => $emit('update:modelValue', v)"
                                   @select="(id) => $emit('select', id)"
                                   @menu-action="(payload) => $emit('menu-action', payload)"
                                   @action="(payload) => $emit('action', payload)"
                                   @finished-render="onItemFinishedRender" />
                </template>
            </div>
        </div>

        <!-- Spacer so bottom section stays at bottom -->
        <div class="icon-drawer__spacer" aria-hidden="true" />

        <!-- Bottom icons: fixed at bottom, can scroll if many -->
        <div class="icon-drawer__bottom" ref="bottomEl">
            <div class="icon-drawer__list">
                <template v-for="(item, index) in bottomItems" :key="item.id ?? index">
                    <div v-if="item.separator"
                         class="icon-drawer__separator"
                         :aria-hidden="true" />
                    <NavDrawerIcon v-else
                                   :item="item"
                                   :row-data="item.rowData"
                                   :control-data="item.controlData ?? {}"
                                   :model-value="modelValue"
                                   :expanded="expanded"
                                   :dense="dense"
                                   :preview-mode="previewMode"
                                   :name="namePrefix ? `${namePrefix}_bottom_${index}` : `bottom_${index}`"
                                   :root="root"
                                   :scopeitems="scopeitems"
                                   :controlscope="controlscope"
                                   @update:model-value="(v) => $emit('update:modelValue', v)"
                                   @select="(id) => $emit('select', id)"
                                   @menu-action="(payload) => $emit('menu-action', payload)"
                                   @action="(payload) => $emit('action', payload)"
                                   @finished-render="onItemFinishedRender" />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import NavDrawerIcon from './NavDrawerIcon.jsx';
    import theme from '@/plugins/theme';

    export default {
        name: 'IconDrawer',
        components: {
            NavDrawerIcon,
        },

        props: {
            /** Width of the drawer (e.g. '56px' or 56). */
            width: { type: [String, Number], default: 56 },
            /** Items: { id, icon, label?, tooltip?, type: 'tab'|'menu'|'action', placement: 'top'|'bottom', menuItems?: [{ label, icon? }], badge?: { showbadge, count } }. placement defaults to 'top'. */
            items: { type: Array, default: () => [] },
            /** Active tab id (for type 'tab' items). v-model. */
            modelValue: { type: [String, Number], default: null },
            /** Optional dark theme for the drawer. */
            dark: { type: Boolean, default: false },
            /** Optional background color (CSS color value). */
            backgroundColor: { type: String, default: null },
            /** When true, drawer is expanded and shows labels next to icons. */
            expanded: { type: Boolean, default: false },
            /** Optional dense icon mode from NavigationDrawer. */
            dense: { type: Boolean, default: false },
            /** Base component context passthrough for child icon components. */
            root: { default: null },
            scopeitems: { default: null },
            controlscope: { default: null },
            namePrefix: { type: String, default: '' },
            /** When true, icon clicks are inert except collapse chevron (designer preview). */
            previewMode: { type: Boolean, default: false },
        },

        emits: ['update:modelValue', 'select', 'menu-action', 'action', 'item-finished-render'],

        data() {
            return {
                collapseTransitionComplete: !this.expanded,
                collapseTransitionTimer: null,
            };
        },

        watch: {
            expanded(val) {
                if (val) {
                    this.collapseTransitionComplete = true;
                    if (this.collapseTransitionTimer) {
                        clearTimeout(this.collapseTransitionTimer);
                        this.collapseTransitionTimer = null;
                    }
                } else {
                    this.collapseTransitionComplete = false;
                    this.collapseTransitionTimer = setTimeout(() => {
                        this.collapseTransitionComplete = true;
                        this.collapseTransitionTimer = null;
                    }, 250);
                }
            },
        },

        unmounted() {
            if (this.collapseTransitionTimer) clearTimeout(this.collapseTransitionTimer);
        },

        computed: {
            drawerStyle() {
                const style = {
                    width: '100%',
                    minWidth: this.expanded ? 0 : '56px',
                    transition: 'none',
                };
                if (this.backgroundColor) {
                    style.backgroundColor = theme.resolveThemeColor(this.backgroundColor);
                    style.color = 'white';
                }
                return style;
            },

            topItems() {
                return this.items.filter((i) => (i.placement ?? 'top') === 'top');
            },

            bottomItems() {
                return this.items.filter((i) => i.placement === 'bottom');
            },
        },

        methods: {
            onItemFinishedRender(reference) {
                this.$emit('item-finished-render', reference);
            },
        },
    };
</script>

<style scoped>
    .icon-drawer {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        height: 100%;
        min-height: 0;
        flex-shrink: 0;
        background: var(--q-color-surface, #fff);
        border-right: 1px solid rgba(0, 0, 0, 0.12);
    }

    .icon-drawer__top,
    .icon-drawer__bottom {
        /* Opt out of global scrollbar-color/width so ::-webkit-scrollbar width is honored
           (same approach as Vue2 `.v-navigation-drawer__content` at 5px). */
        scrollbar-color: auto;
        scrollbar-width: auto;
    }

    .icon-drawer__top {
        flex: 0 1 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 8px 0;
        scrollbar-gutter: stable both-edges;
    }

    .icon-drawer__top::-webkit-scrollbar,
    .icon-drawer__bottom::-webkit-scrollbar {
        width: 5px;
    }

    /* Custom webkit width hides OS arrows; restore single up/down buttons. */
    .icon-drawer__top::-webkit-scrollbar-button:single-button,
    .icon-drawer__bottom::-webkit-scrollbar-button:single-button {
        display: block;
        height: 10px;
        background-repeat: no-repeat;
        background-position: center;
    }

    .icon-drawer__top::-webkit-scrollbar-button:single-button:vertical:decrement,
    .icon-drawer__bottom::-webkit-scrollbar-button:single-button:vertical:decrement {
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='%2364748b'><polygon points='50,15 15,70 85,70'/></svg>");
        background-size: 7px 6px;
    }

    .icon-drawer__top::-webkit-scrollbar-button:single-button:vertical:increment,
    .icon-drawer__bottom::-webkit-scrollbar-button:single-button:vertical:increment {
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='%2364748b'><polygon points='15,30 85,30 50,85'/></svg>");
        background-size: 7px 6px;
    }

    .icon-drawer__top::-webkit-scrollbar-button:vertical:start:increment,
    .icon-drawer__top::-webkit-scrollbar-button:vertical:end:decrement,
    .icon-drawer__bottom::-webkit-scrollbar-button:vertical:start:increment,
    .icon-drawer__bottom::-webkit-scrollbar-button:vertical:end:decrement {
        display: none;
    }

    .icon-drawer__bottom {
        flex: 0 0 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 8px 0;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
    }

    .icon-drawer__spacer {
        flex: 1 1 0;
        min-height: 0;
    }

    .icon-drawer__list {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding-left: 0;
        padding-right: 0;
    }

    .icon-drawer--expanded .icon-drawer__list {
        align-items: stretch;
        padding-left: 8px;
        padding-right: 8px;
    }

    .icon-drawer__item {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        width: 100%;
        height: 40px;
        padding: 0;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: inherit;
        cursor: pointer;
        transition: background-color 0.15s, color 0.15s, gap 0.25s ease;
        flex-shrink: 0;
        gap: 0;
    }

    .icon-drawer--expanded .icon-drawer__item {
        padding-left: 12px;
        padding-right: 12px;
        gap: 12px;
    }

    /* During collapse transition: keep icons at same left inset as expanded (not flush left) */
    .icon-drawer--collapsing .icon-drawer__list {
        padding-left: 8px;
        padding-right: 8px;
    }

    .icon-drawer--collapsing .icon-drawer__item {
        padding-left: 12px;
        padding-right: 12px;
    }

    .icon-drawer--centered .icon-drawer__item {
        justify-content: center;
    }

    :deep(.icon-drawer__label) {
        flex: 0 0 0;
        min-width: 0;
        max-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap !important;
        font-size: 0.875rem;
        text-align: left;
        opacity: 0;
        transition: max-width 0.25s ease, opacity 0.2s ease;
    }

    :deep(.icon-drawer__label--visible) {
        flex: 1 1 0;
        max-width: 100%;
        opacity: 1;
    }

    .icon-drawer__item:hover {
        background-color: rgb(from var(--q-primary-inverse, #fff) r g b / 0.08);
    }

    .icon-drawer__item--active {
        background-color: rgb(from var(--q-primary-inverse, #fff) r g b / 0.24);
        color: var(--q-primary-inverse, #fff);
    }

        .icon-drawer__item--active:hover {
            background-color: rgb(from var(--q-primary-inverse, #fff) r g b / 0.24);
            opacity: 0.92;
        }

    .icon-drawer__separator {
        width: 24px;
        height: 1px;
        margin: 6px 0;
        background: rgba(0, 0, 0, 0.12);
    }

    .icon-drawer--expanded .icon-drawer__separator {
        width: 100%;
    }
</style>
